// src/hooks/useCreditUsage.js
// ---------------------------------------------------------------------------
// Phase 6 (FINAL) — credit usage state for the chat-area alert.
//
// Fetches the read-only usage status from GET /api/credit/usage and drives:
//   * the derived presentation state (NORMAL / CRITICAL / EXHAUSTED);
//   * the single dismissible 90% flag (per usageContext, cleared when the
//     server reports a fresh allocation);
//   * isCreditUnavailable (Send must be disabled while true);
//   * 402 handling (optimistic EXHAUSTED + forced authoritative refresh).
//
// Deliberately tiny: NO threshold-toast state machine, NO sessionStorage
// notification tracking, NO episode/dedup logic, NO modal disclosure.
//
// Kept free of Chakra/react-router imports so it can be unit-tested directly
// in the CRA/jest environment. `fetchFn` and `storage` are injectable.
// ---------------------------------------------------------------------------
import { useCallback, useRef, useState } from 'react';
import {
  USAGE_STATE,
  deriveUsageState,
  dismiss90StorageKey,
  is90Dismissed,
  mark90Dismissed,
  clear90Dismissed,
} from '../utils/creditHelpers';

// Resolve the usage endpoint against the SAME configured API base the rest of
// the client uses (REACT_APP_WEB_SERVER_URL). The usage read must never be a
// relative URL: a relative URL would resolve against the CRA client origin
// (e.g. http://localhost:3000 in local dev) where no /api route exists — the
// dev server would answer with index.html (HTTP 200), JSON parsing would
// fail, and `initialized` would never resolve (Send stays disabled with no
// exhausted alert).
const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

// Approved 60-second staleness window. No polling.
export const STALE_MS = 60 * 1000;

export function useCreditUsage({ fetchFn, storage } = {}) {
  const [data, setData] = useState(null); // { usagePercent, status, usageContext, hasActiveSubscription }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [state, setState] = useState(USAGE_STATE.NORMAL);
  const [dismissed90, setDismissed90] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // Phase 7: tracks whether the initial authoritative usage state has been
  // resolved successfully. While false, the client must NOT allow a new AI
  // request (Send stays disabled) — we refuse to let an already-exhausted
  // user submit before the server-authoritative state is known. It is set
  // true only after a successful usage fetch, and reset on logout.
  const [initialized, setInitialized] = useState(false);

  const lastFetchedAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const getStore = useCallback(
    () => storage || (typeof window !== 'undefined' ? window.sessionStorage : null),
    [storage]
  );

  const isStale = useCallback(() => {
    return Date.now() - lastFetchedAtRef.current > STALE_MS;
  }, []);

  // Sync the single dismissible-90% flag with the server-authoritative state.
  //   * Server reports NORMAL (<90%)  -> clear the dismissal (a new legitimate
  //     credit allocation has been granted, so a future 90% warning is re-armed).
  //   * Otherwise                     -> read the persisted dismissal.
  const syncDismissal = useCallback(
    (derivedState, usageContext) => {
      const key = dismiss90StorageKey(usageContext);
      if (derivedState === USAGE_STATE.NORMAL) {
        clear90Dismissed(key, getStore());
        setDismissed90(false);
      } else {
        setDismissed90(is90Dismissed(key, getStore()));
      }
    },
    [getStore]
  );

  const refresh = useCallback(
    async (force = false) => {
      if (inFlightRef.current) return;
      if (!force && !isStale()) return;

      inFlightRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const fn = fetchFn || (await import('../context/AuthContext')).fetchWithAuth;
        // This is an authoritative state read: the browser must never serve
        // it from its HTTP cache and must never revalidate it with a
        // conditional request (If-None-Match / If-Modified-Since). Passing
        // `cache: 'no-store'` on the REQUEST makes the browser skip its cache
        // entirely, so no responder can answer with a body-less 304 — the
        // server-authoritative EXHAUSTED/AVAILABLE body always comes back.
        const res = await fn(`${API}/api/credit/usage`, { cache: 'no-store' });
        // HTTP 304 (Not Modified) is NOT a usable JSON usage response. It
        // must never be treated as proof of usable credit, and it must never
        // reset the client to NORMAL. We retain the last known authoritative
        // usage state and surface a fetch error instead. (With the
        // cache-bypassing request above a 304 should be impossible — this is
        // a defensive guard.)
        if (res.status === 304) {
          throw new Error('Credit usage not modified (304)');
        }
        if (!res.ok) {
          throw new Error(`Credit usage request failed (${res.status})`);
        }
        const body = await res.json();
        const derived = deriveUsageState(body);
        setData(body);
        setState(derived);
        setHasActiveSubscription(body.hasActiveSubscription === true);
        lastFetchedAtRef.current = Date.now();
        syncDismissal(derived, body.usageContext);
        // Only a successful authoritative fetch marks the initial state as
        // resolved. A failure leaves `initialized` false so Send stays
        // disabled until the server-authoritative state is known.
        setInitialized(true);
      } catch (err) {
        // A failed / 304 refresh must NEVER clear the last known
        // authoritative credit state. `state` is only updated on a successful
        // response, so an existing EXHAUSTED state (and its persistent alert
        // + disabled Send) is retained until the server actually reports
        // AVAILABLE. We simply surface the fetch error and keep the last
        // known state.
        setError(err);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    },
    [fetchFn, isStale, syncDismissal]
  );

  // Handle a 402 from a gated AI request.
  //   * Optimistically mark credit unavailable (instant alert + disabled Send).
  //   * Force an authoritative refresh — the server remains the source of
  //     truth. No modal, no toast, no technical details.
  const handleCredit402 = useCallback(async () => {
    setState(USAGE_STATE.EXHAUSTED);
    await refresh(true);
  }, [refresh]);

  // Dismiss the 90% alert for the current credit-allocation period.
  const dismiss90 = useCallback(() => {
    mark90Dismissed(dismiss90StorageKey(data && data.usageContext), getStore());
    setDismissed90(true);
  }, [data, getStore]);

  // Clear local state on logout. The limit/usage state itself is NOT reset —
  // it re-hydrates from the server on the next authenticated load.
  const resetForLogout = useCallback(() => {
    setData(null);
    setState(USAGE_STATE.NORMAL);
    setDismissed90(false);
    setHasActiveSubscription(false);
    setInitialized(false);
    lastFetchedAtRef.current = 0;
  }, []);

  // Send must be disabled while the server-authoritative state is EXHAUSTED,
  // AND while the initial authoritative state has not yet been resolved
  // successfully (Phase 7: never allow a new AI request before the initial
  // credit state is known — an already-exhausted user must not be able to
  // submit freely during the hydration window).
  const isCreditUnavailable =
    state === USAGE_STATE.EXHAUSTED || !initialized;

  return {
    data,
    loading,
    error,
    state,
    initialized,
    isCreditUnavailable,
    is90AlertVisible: state === USAGE_STATE.CRITICAL && !dismissed90,
    dismissed90,
    hasActiveSubscription,
    refresh,
    handleCredit402,
    dismiss90,
    isStale,
    resetForLogout,
  };
}
